<template>
  <fragment>
    <thead>
      <tr>
        <th
          v-for="col in displayColumns"
          :key="col.id"
          @click="sortTable(col.id)"
        >
          {{ col.display }}
          <font-awesome-icon
            :icon="['fas', 'sort']"
            class="icon alt"
          />
          <span
            v-if="col.id === sortColumn"
            :class="ascending ? 'active-down' : 'active-up'"
          >
            <font-awesome-icon
              :icon="['fas', 'sort-down']"
              class="icon alt"
            />
            <font-awesome-icon
              :icon="['fas', 'sort-up']"
              class="icon alt"
            />
          </span>
        </th>
        <th class="actions">
          <span class="sr-only">Download</span>
        </th>
        <th class="text-right actions">
          <span class="sr-only">Actions</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="device in filteredDevices"
        :key="device.id"
      >
        <td>
          {{ device.serial_number }}
          {{ device.nickname ? `(${device.nickname})` : '' }}
          <label class="d-block d-md-none">{{ columns[0].display }}</label>
        </td>
        <td v-if="$store.getters.isKgbAdminUser">
          {{ device.organization_name }}
          <label class="d-block d-md-none">{{ columns[1].display }}</label>
        </td>
        <td v-if="$store.getters.isAdminUser">
          {{ device.site_name }}
          <label class="d-block d-md-none">{{ columns[2].display }}</label>
        </td>
        <td>
          <span v-if="device.lastCheckIn">
            <span
              class="c-pointer"
              :title="device.lastCheckIn.toUTCString()"
              @click="showCheckinList(device)"
            >
              {{ device.lastCheckIn.toLocaleString() }}
            </span>
          </span>
          <span v-else>
            N/A
          </span>
          <label class="d-block d-md-none">{{ columns[3].display }}</label>
        </td>
        <td>
          <span
            :class="{'error-red': ($store.getters.isAdminUser)
              && device.dartVersion !== 'N/A'
              && device.dartVersion !== device.latestDartVersion}"
          >
            {{ device.dartVersion }}
            <span
              v-if="($store.getters.isAdminUser)
                && device.dartVersion !== 'N/A'
                && device.dartVersion !== device.latestDartVersion"
            >
              <font-awesome-icon
                :icon="['fas', 'exclamation-circle']"
                class="icon alt"
              />
            </span>
            <label class="d-block d-md-none">{{ columns[4].display }}</label>
          </span>
        </td>
        <td>
          <span
            :class="{'error-red': device.disabled}"
          >
            {{ device.status }}
          </span>
          <label class="d-block d-md-none">{{ columns[5].display }}</label>
        </td>
        <td class="actions">
          <b-button
            v-b-tooltip.hover
            :disabled="!device.has_data_download"
            :hidden="!device.has_data_download"
            title="Download Data"
            class="blue-hover black"
            style="margin-right: 3px;"
            ok-only
            @click="showDownloadModal(device)"
          >
            <font-awesome-icon
              :icon="['fas', 'download']"
              class="icon alt"
            />
          </b-button>
        </td>
        <td class="actions">
          <button
            v-if="$store.getters.isKgbAdminUser"
            class="blue-hover"
            title="View History"
            type="button"
            style="margin-right: 3px;"
            @click="viewDeviceHistory(device)"
          >
            <font-awesome-icon :icon="['fa', 'book']" />
          </button>
          <button
            v-if="$store.getters.isKgbAdminUser && device.disabled"
            v-b-tooltip.hover
            class="green-hover"
            title="Enable Device"
            type="button"
            style="margin-right: 3px;"
            @click="enableDevice(device)"
          >
            <font-awesome-icon
              :icon="['fas', 'power-off']"
              class="icon alt"
            />
          </button>
          <button
            v-if="$store.getters.isKgbAdminUser"
            v-b-tooltip.hover
            class="blue-hover"
            title="Edit"
            type="button"
            style="margin-right: 3px;"
            @click="editDevice(device)"
          >
            <font-awesome-icon
              :icon="['fas', 'pencil']"
              class="icon alt"
            />
          </button>
          <button
            v-if="!$store.getters.isKgbAdminUser"
            v-b-tooltip.hover
            class="blue-hover"
            title="View Details"
            type="button"
            style="margin-right: 3px;"
            @click="editDevice(device)"
          >
            <font-awesome-icon
              :icon="['fas', 'pencil']"
              class="icon alt"
            />
          </button>

          <button
            v-if="($store.getters.isAdminUser)
              && device.dartVersion !== 'N/A'
              && device.dartVersion !== device.latestDartVersion"
            v-b-tooltip.hover
            type="button"
            class="blue-hover"
            style="margin-right: 3px;"
            title="Push update"
            @click="verifyUpdateToVersion(device)"
          >
            <font-awesome-icon
              :icon="['fal', 'layer-plus']"
              class="icon alt"
            />
          </button>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="devices.length > recordsPerPage">
      <tr>
        <td :colspan="displayColumns.length + 2">
          <b-pagination
            v-model="currentPage"
            :total-rows="devices.length"
            :per-page="recordsPerPage"
            @change="pageChange"
          />
        </td>
      </tr>
    </tfoot>

    <!-- Checkins Modal -->
    <b-modal
      id="checkin-modal"
      ref="checkinModal"
      title="Last DART Check Ins"
      ok-only
      ok-title="Close"
      size="md"
    >
      <div>
        <div v-if="loadingCheckins">
          <Loader />
        </div>

        <table
          v-if="!loadingCheckins"
          class="table data-download-table"
        >
          <tbody>
            <tr
              v-for="checkin in filteredCheckins"
              :key="checkin.created"
            >
              <td :title="new Date(checkin.created).toUTCString()">
                {{ new Date(checkin.created).toLocaleString() }}
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <b-pagination
            v-if="checkins.length > checkinsPerPage"
            v-model="checkinsPage"
            :total-rows="checkins.length"
            :per-page="checkinsPerPage"
          />
        </div>
      </div>
    </b-modal>

    <!-- Download Modal -->
    <b-modal
      id="download-modal"
      ref="downloadModal"
      title="Download Data"
      ok-only
      ok-title="Close"
      size="xl"
    >
      <div v-if="loadingDownloads">
        <Loader />
      </div>
      <p v-else-if="!downloads || !downloads.length">
        This device does not have any downloads available.
      </p>
      <div v-else>
        <table class="table data-download-table">
          <thead>
            <th>
              Tail #
            </th>
            <th>
              Date
            </th>
            <th>
              File
            </th>
            <th
              class="text-right"
              colspan="3"
            >
              Actions
            </th>
          </thead>
          <tbody>
            <tr
              v-for="download in filteredDownloads"
              :key="download.id"
              :class="{'deleted': download.is_deleted}"
            >
              <td class="no-wrap">
                {{ download.aircraft_tail_number ? download.aircraft_tail_number: 'N/A' }}
              </td>
              <td
                :title="new Date(download.uploaded_time).toUTCString()"
                class="no-wrap"
              >
                {{ download.uploaded_time | date }}
              </td>
              <td class="break-on-word">
                {{ download.filename ? download.filename: 'N/A' }}
              </td>
              <td class="text-right action-option">
                <span
                  v-if="!download.is_deleted && download.fileCanBeDownloaded"
                  class="link"
                  @click="downloadFile(currentDevice, download)"
                >
                  <span
                    style="display: inline-block; margin-right: 8px;"
                  >
                    Download
                  </span>
                  <font-awesome-icon
                    :icon="['fas', 'download']"
                    class="icon alt"
                  />
                </span>
              </td>
              <td class="text-right action-option">
                <span
                  v-if="$store.getters.currentUser.allowed_to_remove_files
                    && !download.is_deleted
                    && download.fileCanBeDownloaded"
                  class="link"
                  @click="removeFile(currentDevice, download)"
                >
                  Delete
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="icon alt"
                  />
                </span>
                <span
                  v-if="download.is_deleted"
                  v-b-popover.hover="buildDeletedByText(download)"
                >
                  Deleted
                  <font-awesome-icon
                    :icon="['fas', 'info-circle']"
                    class="icon alt"
                  />
                </span>
              </td>
              <td class="text-right action-option">
                <span
                  v-if="download.hasBeenDownloaded"
                  class="link"
                  @click="viewHistoryModal(currentDevice, download)"
                >
                  Downloaded By
                  <font-awesome-icon :icon="['fa', 'book']" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <b-pagination
            v-if="downloads.length > downloadsPerPage"
            v-model="downloadPage"
            :total-rows="downloads.length"
            :per-page="downloadsPerPage"
          />
        </div>
      </div>
    </b-modal>

    <b-modal
      id="download-history-modal"
      ref="downloadHistoryModal"
      title="File Downloaded By"
      ok-only
      ok-title="Close"
      size="md"
    >
      <div v-if="loadingDownloadViewHistory">
        <Loader />
      </div>
      <p v-else-if="!downloadHistory || !downloadHistory.length">
        {{ downloadHistoryFileName }} has not been downloaded by any user.
      </p>
      <div v-else>
        <div>
          {{ downloadHistoryFileName }}
        </div>
        <table class="table data-download-table">
          <thead>
            <th>
              User
            </th>
            <th>
              Date
            </th>
          </thead>
          <tbody>
            <tr
              v-for="history in filteredDownloadHistory"
              :key="history.id"
            >
              <td>
                {{ history.created_by_full_name }}
              </td>
              <td :title="new Date(history.created_datetime).toUTCString()">
                {{ history.created_datetime | date }}
              </td>
            </tr>
          </tbody>
        </table>
        <div>
          <b-pagination
            v-if="downloadHistory.length > downloadsPerPage"
            v-model="downloadHistoryPage"
            :total-rows="downloadHistory.length"
            :per-page="downloadsPerPage"
          />
        </div>
      </div>
    </b-modal>
    <!-- Confirmation Modal -->
    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
    <history-modal
      ref="deviceHistoryLog"
      :data-mapping="deviceHistoryMapping"
    />
  </fragment>
</template>

<script>
import Loader from '@/components/Loader.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import HistoryModal from '@/components/modals/HistoryModal.vue';
import paginationHelper from '../../common/paginationHelper';

const enabledMap = value => (value ? 'Enabled' : 'Disabled');

/* eslint-disable vue/require-default-prop */
export default {
  name: 'UserTable',
  components: {
    ConfirmModal,
    HistoryModal,
    Loader,
  },
  props: {
    devices: {
      type: Array,
    },
    organizations: {
      type: Array,
    },
    editDevice: {
      type: Function,
    },
    disableDevice: {
      type: Function,
    },
    enableDevice: {
      type: Function,
    },
    removeFile: {
      type: Function,
    },
    downloadFile: {
      type: Function,
    },
    viewHistory: {
      type: Function,
    },
    getDeviceDownloads: {
      type: Function,
    },
    getDeviceCheckins: {
      type: Function,
    },
    updateToLatestVersion: {
      type: Function,
    },
    getDeviceHistory: {
      type: Function,
    },
    getAllSites: {
      type: Function,
    },
  },
  data: () => ({
    ascending: false,
    currentDevice: null,
    sortColumn: '',
    downloads: [],
    downloadHistory: [],
    downloadHistoryFileName: '',
    displayColumns: [],
    loadingDownloadViewHistory: false,
    loadingCheckins: false,
    loadingDownloads: false,
    checkins: [],
    allSites: null,
    columns: [{
      id: 'serial_number', display: 'Serial ID',
    }, {
      id: 'organization_name', display: 'Organization', kgbAdminOnly: true,
    }, {
      id: 'site_name', display: 'Site', adminOnly: true,
    }, {
      id: 'lastCheckIn', display: 'Last Checkin',
    }, {
      id: 'dartVersion', display: 'DART Version',
    }, {
      id: 'status', display: 'Status',
    },
    ],
    currentPage: 1,
    recordsPerPage: 25,
    downloadPage: 1,
    checkinsPage: 1,
    checkinsPerPage: 10,
    downloadHistoryPage: 1,
    downloadsPerPage: 10,
    confirmTitle: '',
    confirmBody: '',
    confirmAction: () => {},
  }),
  computed: {
    filteredDevices() {
      return this.sortedData()
        .slice(
          (this.currentPage - 1) * this.recordsPerPage,
          this.currentPage * this.recordsPerPage,
        ) || [];
    },
    filteredDownloads() {
      return this.downloads
        .slice(
          (this.downloadPage - 1) * this.downloadsPerPage,
          this.downloadPage * this.downloadsPerPage,
        ) || [];
    },
    filteredCheckins() {
      return this.checkins
        .slice(
          (this.checkinsPage - 1) * this.checkinsPerPage,
          this.checkinsPage * this.checkinsPerPage,
        ) || [];
    },
    filteredDownloadHistory() {
      return this.downloadHistory
        .slice(
          (this.downloadHistoryPage - 1) * this.downloadsPerPage,
          this.downloadHistoryPage * this.downloadsPerPage,
        ) || [];
    },
    deviceHistoryMapping() {
      const organizations = [...this.organizations];
      const allSites = this.allSites ? [...this.allSites] : [];
      return {
        organization: {
          displayName: 'Organization',
          format: (value) => {
            const foundOrg = organizations.find(org => org.id === value);
            if (foundOrg) {
              return foundOrg.name;
            }
            return value;
          },
        },
        site: {
          displayName: 'Site',
          format: (value) => {
            if (value === null) {
              return 'Primary';
            }

            const foundOrg = allSites.find(org => org.id === value);
            if (foundOrg) {
              return foundOrg.name;
            }
            return value;
          },
        },
        utcOffset: { displayName: 'UTC Offset' },
        nickname: { displayName: 'Nickname' },
        disabled: { displayName: 'Status', format: value => (value ? 'Disabled' : 'Enabled') },
        dataMonitoring: { displayName: 'Data Monitoring', format: enabledMap },
        engineeringUnits: { displayName: 'Engineering Units', format: enabledMap },
        webOffload: { displayName: 'Web Offload', format: value => value.charAt(0).toUpperCase() + value.slice(1) },
        ownership: { displayName: 'Ownership', format: value => value.charAt(0).toUpperCase() + value.slice(1) },
        offloadLocation: { displayName: 'Offload Location', format: value => value.charAt(0).toUpperCase() + value.slice(1) },
        archiveData: { displayName: 'Archive Data', format: enabledMap },
        downloadInfoOption: { displayName: 'Download Information Screen' },
        fa2100: { displayName: 'L3 - FA2100/FA2300', format: enabledMap },
        fa5000: { displayName: 'L3 - FA5000', format: enabledMap },
        f1000: { displayName: 'L3 - F1000', format: enabledMap },
        srvivr: { displayName: 'L3 - SRVIVR', format: enabledMap },
        uacvfdr: { displayName: 'Universal 1605/1606/1607', format: enabledMap },
        as4700: { displayName: 'Honeywell 980-4700/980-4710', format: enabledMap },
        hw6020: { displayName: 'Honeywell - 980-6020', format: enabledMap },
        hw6023: { displayName: 'Honeywell - 980-6023', format: enabledMap },
        hw6025: { displayName: 'Honeywell - 980-6025', format: enabledMap },
        hwsrvivr: { displayName: 'Honeywell - SRVIVR 980-6044/980-4131/980-6050', format: enabledMap },
        D51615: { displayName: 'Penny & Giles D51615 MPFR', format: enabledMap },
        hfr5: { displayName: 'Honeywell HFR5-C/D', format: enabledMap },
        fortressCamera: { displayName: 'Fortress Camera', format: enabledMap },
        fortressRecorder: { displayName: 'Fortress Recorder', format: enabledMap },
        l3uqar: { displayName: 'L3 uQAR-USB', format: enabledMap },
        arinc717Transmit: { displayName: '717 Transmit', format: enabledMap },
        arinc717Receive: { displayName: '717 Receive', format: enabledMap },
        arinc429Transmit: { displayName: '429 Transmit', format: enabledMap },
        arinc429Receive: { displayName: '429 Receive', format: enabledMap },
        milStd1553Transmit: { displayName: '1553 Transmit', format: enabledMap },
        milStd1553Receive: { displayName: '1553 Receive', format: enabledMap },
        allowWifi: { displayName: 'WiFi', format: enabledMap },
        allowEthernet: { displayName: 'Ethernet', format: enabledMap },
        allowBluetooth: { displayName: 'Bluetooth', format: enabledMap },
        allowCellular: { displayName: 'Cellular', format: enabledMap },
        allowDemo: { displayName: 'Demo Mode', format: enabledMap },
        allowDemoRemoteView: { displayName: 'Demo Mode Remote View', format: enabledMap },
        is_allowed_data_encryption: { displayName: 'Use Organization PGP Keys for encryption', format: enabledMap },
        addedKey: { displayName: 'Added Encryption Key Name' },
        removedKey: { displayName: 'Removed Encryption Key Name' },
      };
    },
  },
  watch: {
    // eslint-disable-next-line func-names
    '$route.params': function () {
      this.handleRouteParams(this.$route.query);
    },
  },
  mounted() {
    this.displayColumns = this.columns
      .filter(x => ((x.kgbAdminOnly && this.$store.getters.isKgbAdminUser)
      || (x.adminOnly && this.$store.getters.isAdminUser)
      || (!x.adminOnly && !x.kgbAdminOnly)));
    this.handleRouteParams(this.$route.query);
  },
  methods: {
    handleRouteParams(queryParameterObject) {
      this.sortColumn = queryParameterObject.o;
      this.ascending = !!(queryParameterObject.a && queryParameterObject.a.toString() === 'true');
      this.currentPage = queryParameterObject.p || 1;
    },
    pageChange(page) {
      this.updateRoute(page, this.sortColumn, this.ascending);
    },
    updateRoute(page, column, ascending) {
      this.$router.push({
        query: { ...this.$route.query, ...paginationHelper(page, column, ascending) },
      });
    },
    sortTable(col, forceDesc) {
      if (forceDesc) {
        this.ascending = false;
      } else if (this.sortColumn === col) {
        this.ascending = !this.ascending;
      } else {
        this.ascending = true;
      }
      this.sortColumn = col;

      this.updateRoute(this.currentPage, this.sortColumn, this.ascending);
    },
    sortedData() {
      if (!this.sortColumn) {
        return this.devices;
      }
      return [...this.devices].sort((a, b) => {
        if (a[this.sortColumn] === null) {
          return this.ascending ? 1 : -1;
        } if (b[this.sortColumn] === null) {
          return this.ascending ? -1 : 1;
        } if (a[this.sortColumn] > b[this.sortColumn]) {
          return this.ascending ? 1 : -1;
        } if (a[this.sortColumn] < b[this.sortColumn]) {
          return this.ascending ? -1 : 1;
        }
        return 0;
      });
    },
    completeRemoveFile(filename) {
      this.downloads = this.downloads.map((x) => {
        if (x.filename !== filename) {
          return x;
        }
        return {
          ...x,
          is_deleted: true,
          deleted_by: this.$store.getters.currentUser.full_name,
          deleted_date: new Date(),
        };
      });
    },
    buildDeletedByText(download) {
      return `Deleted by ${download.deleted_by} on ${download.deleted_date}`;
    },
    verifyUpdateToVersion(device) {
      this.confirmTitle = 'Update DART Version';
      this.confirmBody = `Are you sure you want to push an update to DART ${device.serial_number}?`;
      this.confirmAction = (whenDone) => {
        this.$refs.confirmModal.hide();
        this.updateToLatestVersion(device);
        whenDone();
      };
      this.$refs.confirmModal.show();
    },
    showCheckinList(device) {
      this.checkins = [];
      this.loadingCheckins = true;
      this.checkinsPage = 1;
      this.$refs.checkinModal.show();
      this.getDeviceCheckins(device).then((x) => {
        this.checkins = x;
        this.loadingCheckins = false;
      });
    },
    showDownloadModal(device) {
      this.downloadPage = 1;
      this.currentDevice = device;
      this.$refs.downloadModal.show();
      this.loadingDownloads = true;
      this.getDeviceDownloads(device).then((downloads) => {
        this.loadingDownloads = false;
        this.downloads = downloads.map(d => ({
          ...d,
          uploaded_time: new Date(d.uploaded_time),
          deleted_by: (d.deleted_by && d.deleted_user_is_admin && !this.$store.getters.isKgbAdminUser) ? 'Admin' : d.deleted_by,
          deleted_date: d.is_deleted ? new Date(d.deleted_date) : null,
        }));
      });
    },
    viewHistoryModal(device, downloadFile) {
      this.loadingDownloadViewHistory = true;
      this.downloadHistoryPage = 1;
      this.downloadHistoryFileName = downloadFile.filename;
      this.$refs.downloadHistoryModal.show();

      this.viewHistory(device, downloadFile.id).then((history) => {
        this.loadingDownloadViewHistory = false;

        this.downloadHistory = history.map(d => ({
          id: d.id,
          created_by_full_name: d.created_by_full_name,
          created_datetime: new Date(d.created_datetime),
          created_by_email: d.created_by_email,
        }));
      });
    },
    viewDeviceHistory(device) {
      if (!this.allSites) {
        this.getAllSites().then((sites) => { this.allSites = sites; });
      }
      this.$refs.deviceHistoryLog.show(this.getDeviceHistory(device.id), `History for ${device.serial_number}`);
    },
  },
};
</script>

<style lang="scss" scoped>
#download-modal {
  table {
    & .no-wrap {
      white-space: nowrap;
    }

    & .break-on-word {
      word-break: break-word;
    }

    & .action-option {
      padding-right: 0;
      padding-left: 0;
    }

    tbody {
      .deleted {
        color: $red;

        .link {
          color: $red;
        }
      }

      td {
        &:first-of-type {
          max-width: 200px;
          word-break: break-all;
        }

        &:last-of-type {
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
